export interface CreateSegmentationConfigRequestDto {
  siteId: string;
  ruleAccessType: RuleAccessType;
  name: string;
  createdDateTime: string;
  createdBy: string;
  segmentationType: SegmentationType;
  segmentName: string;
  segmentValue: string;
  rules: SegmentationRuleDto[];
}

export interface UpdateSegmentationConfigRequestDto {
  id: string;
  siteId: string;
  ruleAccessType: RuleAccessType;
  name: string;
  updatedDateTime: string;
  updatedBy: string;
  segmentationType: SegmentationType;
  segmentName: string;
  segmentValue: string;
  rules: SegmentationRuleDto[];
}

export interface SegmentationRuleDto {
  priority: number;
  targets: SegmentationRuleTargetDto;
}

export interface SegmentationRuleTargetDto {
  value: string;
  type: string;
  conditionalOperator: ConditionalOperator;
}

export interface Rule {
  id: number;
}

export interface RuleTarget {
  rule_id: number;
  target_id: number;
  key?: string | null;
  value: string;
  type: TargetType;
  conditional_operator: string;
}

export enum ConditionalOperator {
  EQUALS = 'equals',
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'does_not_contain',
  EQUALS_CASE_INSENSITIVE = 'equals_case_insensitive',
  MULTI_TYPE_EQUALITY = 'multi_type_equality',
  NOT_EMPTY = 'not_empty'
}

export enum TargetType {
  UTM_MEDIUM = 'UtmMedium',
  UTM_SOURCE = 'UtmSource',
  REFERRER_HOST_NAME = 'ReferrerHostName',
  PAYLOAD_KEY = 'PayloadKey',
  LANDING_PAGE_FULL_URL = 'LandingPageFullUrl',
  UTM_CAMPAIGN = 'UtmCampaign',
  PAYLOAD_VALUE = 'PayloadValue',
  LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEY_AND_VALUE = 'LandingPageUrlAndQueryStringSegmentsKeyAndValue',
  PAYLOAD_KEY_AND_VALUE = 'PayloadKeyAndValue',
  LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEYS = 'LandingPageUrlAndQueryStringSegmentsKeys',
  LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_VALUES = 'LandingPageUrlAndQueryStringSegmentsValues',
  AD_PLATFORM = 'AdPlatform',
  CAMPAIGN_NAME = 'CampaignName',
  LABEL_NAME = 'LabelName',
  LABEL_TYPE = 'LabelType',
  CAMPAIGN_ADVERTISING_CHANNEL_TYPE = 'CampaignAdvertisingChannelType',
  PUBLISHER_PLATFORM = 'PublisherPlatform',
  CAMPAIGN_TYPE = 'CampaignType',
  AD_SET_NAME = 'AdSetName',
  AD_NAME = 'AdName'
}

export enum SegmentationType {
  SESSION = 'Session',
  FILTER = 'Filter'
}

export enum RuleAccessType {
  ADMIN = 'Admin',
  MANUAL = 'Manual'
}

export const getSegmentationType = (segmentationType: string) => {
  switch (segmentationType) {
    case 'Session':
      return SegmentationType.SESSION;
    case 'Filter':
      return SegmentationType.FILTER;
    default:
      throw Error(
        `Could not find segmentation type for given type ${segmentationType}`
      );
  }
};

export const getRuleAccessType = (ruleAccessType: string) => {
  switch (ruleAccessType) {
    case 'Manual':
      return RuleAccessType.MANUAL;
    case 'Admin':
      return RuleAccessType.ADMIN;
    default:
      throw Error(
        `Could not find rule access type for given type ${ruleAccessType}`
      );
  }
};

export const getTargetType = (targetType: string): TargetType => {
  switch (targetType) {
    case 'UtmMedium':
      return TargetType.UTM_MEDIUM;
    case 'UtmSource':
      return TargetType.UTM_SOURCE;
    case 'ReferrerHostName':
      return TargetType.REFERRER_HOST_NAME;
    case 'PayloadKey':
      return TargetType.PAYLOAD_KEY;
    case 'LandingPageFullUrl':
      return TargetType.LANDING_PAGE_FULL_URL;
    case 'UtmCampaign':
      return TargetType.UTM_CAMPAIGN;
    case 'PayloadValue':
      return TargetType.PAYLOAD_VALUE;
    case 'LandingPageUrlAndQueryStringSegmentsKeyAndValue':
      return TargetType.LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEY_AND_VALUE;
    case 'PayloadKeyAndValue':
      return TargetType.PAYLOAD_KEY_AND_VALUE;
    case 'LandingPageUrlAndQueryStringSegmentsKeys':
      return TargetType.LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEYS;
    case 'LandingPageUrlAndQueryStringSegmentsValues':
      return TargetType.LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_VALUES;
    case 'AdPlatform':
      return TargetType.AD_PLATFORM;
    case 'CampaignName':
      return TargetType.CAMPAIGN_NAME;
    case 'LabelName':
      return TargetType.LABEL_NAME;
    case 'LabelType':
      return TargetType.LABEL_TYPE;
    case 'CampaignAdvertisingChannelType':
      return TargetType.CAMPAIGN_ADVERTISING_CHANNEL_TYPE;
    case 'PublisherPlatform':
      return TargetType.PUBLISHER_PLATFORM;
    case 'CampaignType':
      return TargetType.CAMPAIGN_TYPE;
    case 'AdSetName':
      return TargetType.AD_SET_NAME;
    case 'AdName':
      return TargetType.AD_NAME;
    default:
      throw new Error(
        `Could not find target type for given type ${targetType}`
      );
  }
};

export const getConditionalOperator = (operator: any): ConditionalOperator => {
  switch (operator) {
    case 0:
      return ConditionalOperator.EQUALS;
    case 1:
      return ConditionalOperator.CONTAINS;
    case 2:
      return ConditionalOperator.DOES_NOT_CONTAIN;
    case 3:
      return ConditionalOperator.EQUALS_CASE_INSENSITIVE;
    case 4:
      return ConditionalOperator.MULTI_TYPE_EQUALITY;
    case 5:
      return ConditionalOperator.NOT_EMPTY;
    default:
      throw new Error(
        `Could not find conditional operator for given type ${operator}`
      );
  }
};
